import axios from "axios";
import { orderStatusConfig } from "../../../types/types";
import { ChannelkartNetworkGet } from "../../../services/networkService";
import { getDataFromLocalDb } from "../../../services/dbService";
import { getUomLabel } from "../../../components/dms_common/DmsUtils";

export const getAuditTrailsStockdata = async (fromDate?: string, toDate?: string) => {
    try {
        // Get user ID from localStorage
        const { caseUnitLabel, otherUnitLabel, packUnitLabel } = getUomLabel();
        const userId = JSON.parse(localStorage.authContext).user.loginId;
        
        let allData: any[] = [];
        let page = 0;
        let hasMoreData = true;
        const pageSize = 5000; 
        
        while (hasMoreData) {
            // Only include user ID filter if the user is not admin
            const userFilter = userId === 'admin@applicate.in' ? '' : `&additionalFilters=userid:${userId}`;
            const url = `/getAuditLogs?fromDate=${fromDate}&toDate=${toDate}&transactionName=stock&additionalFields=caseqty,pieceqty,otherqty,currentcaseqty,currentpieceqty,currentotherqty,skucode,batchid,fromstatus,tostatus,reverttype${userFilter}&page=${page}&size=${pageSize}`;

            const response = await ChannelkartNetworkGet(url);
            const responseData = response?.data || [];
            
            if (Array.isArray(responseData) && responseData.length > 0) {
                allData = [...allData, ...responseData];
                
                if (responseData.length < pageSize) hasMoreData = false; 
                page++; 
            } else hasMoreData = false;
        }

        let productData: any = {};
        try {
            productData = (await getDataFromLocalDb("Product")).data[0].data;
        } catch (e) { }
        allData.forEach((data: any) => {
            const productDetails = productData[data.skucode]
            let nq = calculateNq(data,productDetails);
            let currentQtyNq = calculateCurrentQtyNq(data,productDetails);
            if(caseUnitLabel && productDetails.caseToPieceQuantity){
                data.caseqty = Math.floor(nq / productDetails.caseToPieceQuantity);
                nq%=productDetails.caseToPieceQuantity;
                data.currentcaseqty = Math.floor(currentQtyNq / productDetails.caseToPieceQuantity);
                currentQtyNq%=productDetails.caseToPieceQuantity;
            }
            if(otherUnitLabel && productDetails.otherUnitToPieceQuantity){
                data.otherqty = Math.floor(nq / productDetails.otherUnitToPieceQuantity);
                nq%=productDetails.otherUnitToPieceQuantity;
                data.currentotherqty = Math.floor(currentQtyNq / productDetails.otherUnitToPieceQuantity);
                currentQtyNq%=productDetails.otherUnitToPieceQuantity;
            }
            if(packUnitLabel){
                data.pieceqty = nq;
                data.currentpieceqty = currentQtyNq;
            }
        })
        
        return allData;
    } catch (error) {
        console.error("Error fetching Audit Trails Data", error);
        return [];
    }
};

const calculateCurrentQtyNq = (data:any, productDetails: any) => {
    let nq = 0;
    if (productDetails) {
        nq = data.currentcaseqty * productDetails.caseToPieceQuantity + data.currentpieceqty + data.currentotherqty * productDetails.otherUnitToPieceQuantity;
    }
    return nq;
}
const calculateNq = (data:any, productDetails: any) => {
    let nq = 0;
    if (productDetails) {
        nq = data.caseqty * productDetails.caseToPieceQuantity + data.pieceqty + data.otherqty * productDetails.otherUnitToPieceQuantity;
    }
    return nq;
}

export const deriveStatus = (from: string, to: string, revertType?: string | null): string => {
    // If revertType is not null or empty, return "Revert"
    if (revertType) {
      return "Revert";
    }
    
    const fromStatus = from?.toLowerCase();
    const toStatus = to?.toLowerCase();
  
    if (fromStatus === "n/a" && (toStatus === "available" || toStatus === "unsellable")) {
      return "Stock In";
    } else if ((fromStatus === "onroute" || fromStatus === "committed") && toStatus === "n/a") {
      return "Stock Out";
    } else {
      return "Internal Audit";
    }
};

export const statusMapping: orderStatusConfig[] = [
    {
        id: "Stock In",
        dmsLabelColor: "#17B4A3",
        dmsLabel: "Stock In",
        dmsColor: "#F0FBFF",
    },
    {
        id: "Stock Out",
        dmsLabelColor: "#C44C36",
        dmsLabel: "Stock Out",
        dmsColor: "#FFFAF7",
    },
    {
        id: "Internal Audit",
        dmsLabelColor: "#CC911F",
        dmsLabel: "Internal Audit",
        dmsColor: "#FFF9EF",
    },
    {
        id: "Revert",
        dmsLabelColor: "#128EEB",
        dmsLabel: "Revert",
        dmsColor: "#EDF7FF",
    }
]

export const auditTrailsMetadata = {
    columns: (() => {
        const allColumns = [
            {
                headerName: "Timestamp",
                mappedValue: "timestamp",
                width: 180,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Transaction Type",
                mappedValue: "transactionType",
                width: 240,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "SKU Code",
                mappedValue: "skuCode",
                width: 150,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "SKU Name",
                mappedValue: "skuName",
                width: 240,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Quantity",
                mappedValue: "caseQty,pieceQty,otherQty",
                width: 160,
                headerAlign: "left",
                align: "left",
                qtyDisplay: true
            },
            {
                headerName: "Stock In/Out",
                mappedValue: "status",
                width: 160,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Current Stock Level",
                mappedValue: "currentCaseQty,currentPieceQty,currentOtherQty",
                width: 160,
                headerAlign: "left",
                align: "left",
                qtyDisplay: true
            },
            {
                headerName: "Stock Type From",
                mappedValue: "stockTypeFrom",
                width: 160,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Stock Type To",
                mappedValue: "stockTypeTo",
                width: 160,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "User ID",
                mappedValue: "userId",
                width: 160,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Transaction ID",
                mappedValue: "transactionId",
                width: 160,
                headerAlign: "left",
                align: "left",
            },
            {
                headerName: "Batch ID",
                mappedValue: "batchId",
                width: 180,
                headerAlign: "left",
                align: "left",
            },
        ];

        // Check if batch ID should be shown based on the configuration
        const showBatchIdValue = localStorage.getItem("showBatchId");
        const showBatchId = showBatchIdValue !== "false";
        
        // If showBatchId is "false", filter out the Batch ID column
        return showBatchId 
          ? allColumns 
          : allColumns.filter(column => column.headerName !== "Batch ID");
    })(),
    Actions: [],
};